import axios from 'axios'

import type { CrawlerJobId, ImportSitemapData } from '@/types'
import recaptcha from '../components/App/RecaptchaWidget/recaptcha'

type CrawlerJobList = {
  started: string | null
  finished: string | null
}

type CrawlerJobResponse = { id: string; parsed_url: string; result: string }

type CrawlerActiveJobResponse = {
  data: ImportSitemapData
}

class CrawlerClient {
  instance = axios.create({
    baseURL: import.meta.env.VITE_GENERATOR_URL!,
  })

  async withRecaptha(params: URLSearchParams = new URLSearchParams()) {
    params.append(
      'response_key',
      await recaptcha.execute({ action: 'crawler_client' })
    )
    return params
  }

  async hasJobs(userId: string): Promise<boolean> {
    const { started, finished } = await this.getJobs(userId)
    return !!started || !!finished
  }

  async getJobs(userId: string): Promise<CrawlerJobList> {
    const { data } = await this.withRecaptha(
      new URLSearchParams({
        userId,
      })
    ).then((params) => this.instance.get(`/jobs?${params}`))

    const { finished, started } = data

    return {
      started: started && started.length && started[0],
      finished: finished && finished.length && finished[0],
    }
  }

  async getFinishedJob(jobId: string): Promise<CrawlerJobResponse> {
    const { data } = await this.withRecaptha(
      new URLSearchParams({
        jobId,
      })
    ).then((params) => this.instance.get(`/jobs/finished?${params}`))

    return data
  }

  async removeFinishedJob(jobId: CrawlerJobId) {
    return this.withRecaptha().then((params) =>
      this.instance.post(`/jobs/remove?${params}`, {
        jobId,
      })
    )
  }

  async stopActiveJob(
    jobId: string,
    userId: string
  ): Promise<CrawlerActiveJobResponse> {
    const { data } = await this.withRecaptha().then((params) =>
      this.instance.post(`/jobs/stop?${params}`, {
        jobId,
        userId,
      })
    )

    return data
  }

  async cancelActiveJob(jobId: string, userId: string) {
    return this.withRecaptha().then((params) =>
      this.instance.post(`/jobs/cancel?${params}`, {
        jobId,
        userId,
      })
    )
  }
}

const crawlerClient = new CrawlerClient()

export { crawlerClient }
